import { Component, OnDestroy, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { cloneDeep } from 'lodash-es';
import * as moment from 'moment';
import { Observable, Subscription } from 'rxjs';
import { PropertyElement } from '../../../../../domain/consts';
import { CalculationSubject, Dictionary, DictionaryType, PropertyType } from '../../../../../domain/dictionary.model';
import { ValidationError } from '../../../../../domain/error.model';
import {
    InsuredProductProperty,
    PropertyAttribute,
    PropertyConfigurationStep,
    PropertyFlatElements,
    PropertyHouseElements
} from '../../../../../domain/product.domain';
import { ErrorAwareComponent, LsnErrorStateMatcher } from '../../../../shared/middleware/error-state-matcher';
import { UtilsService } from '../../../service/utils.service';
import { AsideAction } from '../../../store/aside.action';
import { DictionaryState } from '../../../store/dictionary.state';
import { ErrorsState } from '../../../store/errors.state';
import { OfferAction } from '../../../store/offer.action';
import { OfferState } from '../../../store/offer.state';

@Component({
    selector: 'app-property-config-aside',
    templateUrl: './property-config-aside.component.html'
})
export class PropertyConfigAsideComponent implements OnInit, OnDestroy, ErrorAwareComponent {


    subscriptions: Subscription[] = [];
    @Select(DictionaryState.getDictionaries) dictionaries$: Observable<{ [key: string]: Dictionary }>;
    dictionaryTypes = DictionaryType;
    calculationSubject = CalculationSubject;
    errors: ValidationError[] = [];

    product: InsuredProductProperty;
    calculated: InsuredProductProperty;

    get productCalculated() {
        const json1 = UtilsService.stringify(this.product);
        const json2 = UtilsService.stringify(this.calculated);
        return json1 === json2;
    }

    attributes = [
        {type: PropertyAttribute.IS_RENTED, label: 'OFFER_CONFIG.IS_RENTED'},
        {type: PropertyAttribute.HAS_SAFE_DOOR, label: 'OFFER_CONFIG.HAS_SAFE_DOOR'},
        {type: PropertyAttribute.HAS_ALARM, label: 'OFFER_CONFIG.HAS_ALARM'},
        {type: PropertyAttribute.IS_UNDER_PROTECTION, label: 'OFFER_CONFIG.IS_UNDER_PROTECTION'},
    ];

    get elements() {
        const code = this.product.configuration.propertyType
            ? this.product.configuration.propertyType.code
            : PropertyType.HOUSE;
        return code === PropertyType.HOUSE
            ? PropertyHouseElements
            : PropertyFlatElements;
    }

    matchers = {
        continuation: new LsnErrorStateMatcher(this, 'continuation.selected'),
        previousPolicyNumber: new LsnErrorStateMatcher(this, 'continuation.policyNumber'),
        insuranceStartDate: new LsnErrorStateMatcher(this, 'insuranceStartDate'),
        insuranceEndDate: new LsnErrorStateMatcher(this, 'insuranceEndDate'),
        propertyType: new LsnErrorStateMatcher(this, 'propertyType'),
        elementListMatcher: new LsnErrorStateMatcher(this, 'elementList'),
        calculation: new LsnErrorStateMatcher(this, 'calculation'),

        elementList: Object.keys(PropertyElement).reduce((prev, item) => {
            prev[item] = {
                selected: new LsnErrorStateMatcher(this, `elementList[${item}].selected`),
                fire: new LsnErrorStateMatcher(this, `elementList[${item}].fire`),
                theft: new LsnErrorStateMatcher(this, `elementList[${item}].theft`),
            };
            return prev;
        }, {}),
        floodRisk: {
            selected: new LsnErrorStateMatcher(this, 'floodRisk.selected'),
            claims: new LsnErrorStateMatcher(this, 'floodRisk.claims'),
        },
        homeAssistance: {
            selected: new LsnErrorStateMatcher(this, 'homeAssistance.selected'),
        },
        personalLiability: {
            selected: new LsnErrorStateMatcher(this, 'personalLiability.selected'),
            insuredPersonCount: new LsnErrorStateMatcher(this, 'personalLiability.insuredPersonCount'),
        },
        paymentDefinition: {
            installments: new LsnErrorStateMatcher(this, 'paymentDefinition.installments'),
            deferredPayment: new LsnErrorStateMatcher(this, 'paymentDefinition.deferredPayment'),
        }
    };

    propertyConfigurationStep = PropertyConfigurationStep;
    propertyTypes = PropertyType;

    get hasMainInsurance() {
        return this.product.configuration.elementList[PropertyElement.PROPERTY].selected
            || this.product.configuration.elementList[PropertyElement.EQUIPMENT].selected
            || this.product.configuration.elementList[PropertyElement.PERSONAL_BELONGING].selected;
    };

    get continuation() {
        return this.product.configuration.continuation.selected
            ? CalculationSubject.CONTINUATION
            : CalculationSubject.NEW_POLICY;
    }

    set continuation($event: any) {
        this.product.configuration.continuation.selected = $event === CalculationSubject.CONTINUATION;
    }

    set propertyType(value) {
        this.product.configuration.propertyType = {code: value};
        if (value === this.propertyTypes.HOUSE) {
            this.product.configuration.address.apartmentNumber = null;
        }
    }

    set insuranceStartDate(value) {
        this.product.configuration.insuranceStartDate = value;
        this.product.configuration.insuranceEndDate = value !== null
            ? moment(value).add(1, 'year').subtract(1, 'day').format('YYYY-MM-DD')
            : null;
    };

    constructor(private store: Store) {
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.store.select(OfferState.getPropertyProduct).subscribe(product => {
                this.product = cloneDeep(product);
            }),
            this.store.select(OfferState.getPropertyCalculated).subscribe(calculated => {
                this.calculated = cloneDeep(calculated);
            }),
            this.store.select(ErrorsState.getAsideErrors).subscribe(errors => this.errors = errors)
        );
    }


    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    close() {
        this.store.dispatch(AsideAction.CloseRight());
    }


    back() {
        this.product.configuration.configurationStep = PropertyConfigurationStep.BASIC;
    }

    next() {
        const offer = this.store.selectSnapshot(OfferState.getOffer);
        this.store.dispatch(new OfferAction.ValidationPropertyBasic(offer.id, this.product));
    }

    calculate() {
        const offer = this.store.selectSnapshot(OfferState.getOffer);
        this.store.dispatch(new OfferAction.CalculateProperty(offer.id, this.product));
    }

    save() {
        const offer = this.store.selectSnapshot(OfferState.getOffer);
        this.store.dispatch(new OfferAction.SaveProperty(offer.id, this.product));
    }

    setElementSelected(type: PropertyElement, $event: any) {
        this.product.configuration.elementList[type].selected = $event;
        if (!this.hasMainInsurance) {
            this.product.configuration.floodRisk.selected = false;
            this.product.configuration.personalLiability.selected = false;
            this.product.configuration.homeAssistance.selected = false;
        }

    }
}
